import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";

// Contexts
import { ThemeContext } from "../../constant/theme";
import { LanguageContext } from "../../constant/LanguageContext";

// Constants / translations
import { text } from "./constants/translations";

// Hooks
import useResponsive from "./hooks/useResponsive";
import useEscalations from "./hooks/useEscalations";
import useStyles from "./styles/useStyles";

// Components
// Components
import AlertModal from "../../components/AlertModal";
import StatusBadge from "./components/StatusBadge";
import PriorityBadge from "./components/PriorityBadge";
import CreateEscalationModal from "./components/CreateEscalationModal";
import MyEscalationsTable from "./components/MyEscalationsTable";
import GroupAssignedEscalation from "./components/GroupAssignedEscalation";
import { SkeletonStyles, SkeletonBox, LoadingPill } from "../../components/SkeletonLoader";

const EscalationComplaints = () => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);

  // ── Responsive ───────────────────────────────────────────
  const { screenSize, spacing, fonts } = useResponsive();

  // ── Styles ───────────────────────────────────────────────
  const styles = useStyles({ theme, screenSize, spacing, fonts, language });

  // ── Data / state ─────────────────────────────────────────
  const {
    escalations,
    assignedEscalations,
    groups,
    selectedGroup,
    loading,
    error,
    success,
    stats,
    setSelectedGroup,
    setError,
    setSuccess,
    fetchMyEscalations,
    fetchGroups,
    fetchEscalations,
    handleSubmitEscalation,
    handleUpdateStatus,
  } = useEscalations();

  // ── Local UI state ────────────────────────────────────────
  // Notification deep-link: ?view=my|assigned picks the section, and
  // ?escalationId=... auto-opens that complaint's detail modal (passed
  // down as focusEscalationId — the modal state lives in the sections).
  const [searchParams] = useSearchParams();
  const initialView = searchParams.get("view") === "assigned" ? "assigned" : "my";
  const focusEscalationId = searchParams.get("escalationId");
  const [activeTab, setActiveTab] = useState(initialView);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [hoveredTab, setHoveredTab] = useState(null);

  // ── Shorthand translations ────────────────────────────────
  const t = text[language];

  // ── Effects ───────────────────────────────────────────────
  useEffect(() => {
    if (activeTab === "my") fetchMyEscalations();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "assigned") fetchGroups();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "assigned" && groups.length > 0) fetchEscalations();
  }, [selectedGroup, groups, activeTab]);

  // ── initial page skeleton — only until the first successful load; later
  // `loading` toggles (create/update actions) won't re-trigger the skeleton.
  const [pageReady, setPageReady] = useState(false);
  useEffect(() => {
    if (!loading) setPageReady(true);
  }, [loading]);

  // ── Handlers ─────────────────────────────────────────────
  const handleCreate = () => setShowCreateModal(true);

  const handleSubmit = async (data) => {
    await handleSubmitEscalation(data);
    setShowCreateModal(false);
  };

  // ── Badge wrappers (pass theme/screen/fonts automatically) ─
  const BoundStatusBadge = ({ status }) => (
    <StatusBadge status={status} t={t} screenSize={screenSize} fonts={fonts} />
  );

  const BoundPriorityBadge = ({ priority }) => (
    <PriorityBadge
      priority={priority}
      t={t}
      screenSize={screenSize}
      fonts={fonts}
    />
  );

  // ── Initial skeleton ─────────────────────────────────────
  if (!pageReady && loading) {
    const isRTL = language === "ar";
    return (
      <div style={styles.container}>
        <SkeletonStyles theme={theme} />
        <LoadingPill
          text={isRTL ? "جارٍ تحميل التصعيدات..." : "Loading escalations information..."}
          theme={theme}
          isRTL={isRTL}
        />

        {/* Header skeleton */}
        <div
          style={{
            background: theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
            padding: "32px",
            borderRadius: "24px",
            border: theme === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.05)",
            marginBottom: "22px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: "24px",
            direction: isRTL ? "rtl" : "ltr",
          }}
        >
          <div>
            <SkeletonBox width="200px" height="30px" style={{ marginBottom: "12px" }} />
            <SkeletonBox width="280px" height="18px" />
          </div>
          <SkeletonBox width="160px" height="44px" style={{ borderRadius: "12px" }} />
        </div>

        {/* Tabs skeleton */}
        <div style={styles.tabContainer}>
          <SkeletonBox width="140px" height="40px" style={{ borderRadius: "10px" }} />
          <SkeletonBox width="180px" height="40px" style={{ borderRadius: "10px" }} />
        </div>

        {/* Table skeleton */}
        <div style={styles.card1}>
          <SkeletonBox width="220px" height="24px" style={{ marginBottom: "20px" }} />
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <SkeletonBox key={i} width="100%" height="56px" style={{ borderRadius: "12px" }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────
  return (
    <div style={styles.container}>
      {/* Alerts */}
      <AlertModal
        open={!!error}
        title={t.errorTitle || (language === "ar" ? "خطأ" : "Error")}
        message={error}
        variant="error"
        onClose={() => setError("")}
        theme={theme}
        language={language}
      />
      <AlertModal
        open={!!success}
        title={t.successTitle || (language === "ar" ? "نجاح" : "Success")}
        message={success}
        variant="success"
        onClose={() => setSuccess("")}
        theme={theme}
        language={language}
      />

      {/* Header */}
      <style>{`
        @keyframes slideInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      <div
        style={{
          background:
            theme === "dark"
              ? "rgba(255,255,255,0.02)"
              : "rgba(255,255,255,0.9)",
          padding: "32px",
          borderRadius: "24px",
          border:
            theme === "dark"
              ? "1px solid rgba(255,255,255,0.15)"
              : "1px solid rgba(0,0,0,0.05)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          boxShadow:
            theme === "dark"
              ? "0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)"
              : "0 8px 32px rgba(0,0,0,0.1)",
          animation: "slideInDown 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)",
          transition: "all 0.3s ease",
          marginBottom: "22px",
          marginTop: "-36px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: "24px",
          direction: language === "ar" ? "rtl" : "ltr",
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: "28px",
              fontWeight: 700,
              color: theme === "dark" ? "#f1f5f9" : "#1e293b",
              letterSpacing: "-0.5px",
            }}
          >
            {t.title}
          </h1>
          <p
            style={{
              margin: "12px 0 0 0",
              fontSize: "15px",
              fontWeight: 500,
              color: theme === "dark" ? "#cbd5e1" : "#64748b",
            }}
          >
            {t.subtitle || "Manage and track escalations"}
          </p>
        </div>
        <button
          style={{
            background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
            color: "#fff",
            border: "none",
            borderRadius: "12px",
            padding: "12px 20px",
            fontSize: "14px",
            fontWeight: 700,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            boxShadow: "0 4px 14px 0 rgba(37, 99, 235, 0.24)",
            transition: "all 0.3s ease",
            whiteSpace: "nowrap",
          }}
          onClick={handleCreate}
          
        >
          <Plus size={screenSize === "xs" ? 18 : 20} />
          {t.createEscalation}
        </button>
      </div>

      {/* Tabs */}
      <div style={styles.tabContainer}>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === "my" ? styles.activeTab : {}),
            ...(hoveredTab === "my" && activeTab !== "my"
              ? {
                  background:
                    theme === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.04)",
                  color:
                    theme === "dark"
                      ? "rgba(255,255,255,0.75)"
                      : "rgba(26,26,26,0.8)",
                  transform: "translateY(-2px)",
                }
              : {}),
          }}
          onClick={() => setActiveTab("my")}
          // onMouseEnter={() => setHoveredTab("my")}
          // onMouseLeave={() => setHoveredTab(null)}
        >
          {t.myEscalations}
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === "assigned" ? styles.activeTab : {}),
            ...(hoveredTab === "assigned" && activeTab !== "assigned"
              ? {
                  background:
                    theme === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.04)",
                  color:
                    theme === "dark"
                      ? "rgba(255,255,255,0.75)"
                      : "rgba(26,26,26,0.8)",
                  transform: "translateY(-2px)",
                }
              : {}),
          }}
          onClick={() => setActiveTab("assigned")}
          // onMouseEnter={() => setHoveredTab("assigned")}
          // onMouseLeave={() => setHoveredTab(null)}
        >
          {t.assignedEscalations}
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === "my" && (
        <MyEscalationsTable
          loading={loading}
          escalations={escalations}
          stats={stats}
          screenSize={screenSize}
          language={language}
          t={t}
          theme={theme}
          styles={styles}
          fonts={fonts}
          StatusBadge={BoundStatusBadge}
          focusEscalationId={focusEscalationId}
        />
      )}

      {activeTab === "assigned" && (
        <GroupAssignedEscalation
          assignedEscalations={assignedEscalations}
          groups={groups}
          selectedGroup={selectedGroup}
          setSelectedGroup={setSelectedGroup}
          loading={loading}
          onUpdateStatus={handleUpdateStatus}
          theme={theme}
          screenSize={screenSize}
          language={language}
          t={t}
          styles={styles}
          fonts={fonts}
          spacing={spacing}
          StatusBadge={BoundStatusBadge}
          focusEscalationId={focusEscalationId}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateEscalationModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleSubmit}
          loading={loading}
          theme={theme}
          screenSize={screenSize}
          styles={styles}
          language={language}
          t={t}
        />
      )}
    </div>
  );
};

export default EscalationComplaints;
